import type {
  BuildingInstanceV1,
  CaseManifestV1,
  ConditionalInvestigationV1,
} from "@cold-case/shared/case-manifest";
import { npcDisplayName } from "@cold-case/shared/manifest-display";

import {
  BUILDING_ACCESS_DENIED_LEAD,
  BUILDING_EXTERIOR_ENTER_PROMPT,
} from "../content/building-permission-copy.js";

import { hasBuildingClearance } from "./building-clearance.js";
import type { CoherenceJournal } from "./dialog-parser.js";
import { simpleInvestigationConditionMet } from "./dialog-parser.js";

function firstMatchingConditional(
  rows: ConditionalInvestigationV1[] | undefined,
  journal: CoherenceJournal
): ConditionalInvestigationV1 | null {
  if (!rows?.length) return null;
  for (const row of rows) {
    if (simpleInvestigationConditionMet(row.condition, journal)) return row;
  }
  return null;
}

export type BuildingInvestigationResult = {
  /** When false, do not mark building visited or grant clues. */
  permitted: boolean;
  narrative: string;
  clueIdsToReveal: string[];
  permissionHint?: string;
  /** When denied, shown above `narrative` (the manifest permission_hint). */
  denialLead?: string;
};

export type BuildingInvestigationSurface = "exterior" | "interior_search";

export type BuildingInvestigationOpts = {
  decor?: boolean;
  manifest?: CaseManifestV1;
  userId?: string;
  playerCount?: number;
  /** Exterior interactions must not reveal search outcomes or clues. */
  surface?: BuildingInvestigationSurface;
};

/**
 * Resolves manifest `investigation` for a building visit: permission gate,
 * conditional branches, then default narrative + `clues_found`.
 */
export const VACANT_BUILDING_NARRATIVE =
  "Doesn't appear anything important is happening here.";

export function resolveBuildingInvestigation(
  building: BuildingInstanceV1 | null,
  journal: CoherenceJournal,
  opts?: BuildingInvestigationOpts
): BuildingInvestigationResult {
  const surface = opts?.surface ?? "interior_search";

  if (opts?.decor || !building) {
    return {
      permitted: true,
      narrative: VACANT_BUILDING_NARRATIVE,
      clueIdsToReveal: [],
    };
  }
  const inv = building.investigation;
  if (!inv) {
    return {
      permitted: true,
      narrative: VACANT_BUILDING_NARRATIVE,
      clueIdsToReveal: [],
    };
  }

  const perm = inv.requires_permission_from?.trim();
  if (perm) {
    const cleared = hasBuildingClearance(journal, perm, {
      userId: opts?.userId,
      playerCount: opts?.playerCount,
    });
    if (!cleared) {
      const approver =
        opts?.manifest != null
          ? npcDisplayName(opts.manifest, perm)
          : "someone who can authorize access";
      const hint =
        inv.permission_hint?.trim() ||
        `You need approval from ${approver} before you can search here.`;
      return {
        permitted: false,
        narrative: hint,
        clueIdsToReveal: [],
        permissionHint: hint,
        denialLead: BUILDING_ACCESS_DENIED_LEAD,
      };
    }
  }

  if (surface === "exterior") {
    return {
      permitted: true,
      narrative: BUILDING_EXTERIOR_ENTER_PROMPT,
      clueIdsToReveal: [],
    };
  }

  const condHit = firstMatchingConditional(inv.conditional_results, journal);
  const clues = new Set<string>();
  if (condHit?.reveals_clue) clues.add(condHit.reveals_clue);
  if (condHit) {
    return {
      permitted: true,
      narrative: condHit.result.trim() || inv.default_result.trim(),
      clueIdsToReveal: [...clues],
    };
  }

  for (const c of inv.clues_found ?? []) clues.add(c);
  return {
    permitted: true,
    narrative: inv.default_result.trim() || "You find nothing notable.",
    clueIdsToReveal: [...clues],
  };
}

